import Cookies from 'js-cookie';
import { useRouter } from 'next/navigation';
import React, { useEffect, useRef, useState } from 'react';

import { getAdsTracking, getSkipCallCenter } from '@/app/utils/lms';
import { scrollTo } from '@/components/hooks/hash';
import { trackIsuzuCare, trackLead, trackTeletec } from '@/components/hooks/td';
import { Button } from '@/components/shared/buttons/button';
import { Link } from '@/components/shared/element/link';
import { Text } from '@/components/shared/element/text';
import Tooltip from '@/components/shared/element/tooltip';
import { Checkbox } from '@/components/shared/form/checkbox';
import { Radio } from '@/components/shared/form/radio';
import { Select } from '@/components/shared/form/select';
import { TextField } from '@/components/shared/form/text-field';
import { Box } from '@/components/shared/layout/box';
import { Grid } from '@/components/shared/layout/grid';
import { useInViewRef } from '@/components/shared/utility/hooks';
import {
  REGEX_FULLNAME,
  REGEX_ONLY_NUMBER,
  REGEX_TEL_HOME_MOBILE,
} from '@/components/shared/utility/regex';
import { THANK } from '@/const/cookie';
import {
  ISUZU_CARE_FORM_ID,
  MAXFORCE_FORM_ID,
  REGISTER_FORM_ID,
  REGISTER_LCV_FORM_ID,
  TELETEC_FORM_ID,
} from '@/const/form';
import { AVAILABLE_TIME_OPTIONS } from '@/const/register';
import { ERROR_MSG } from '@/const/text';
import logger from '@/lib/logger';
import clientContentStack from '@/services/contentstack/client';
import clientLead from '@/services/lead/client';
import {
  formatModelForFacebookTracking,
  getLeadIDForAcessTrade,
  trackFBLeadFinancial,
  trackFBLeadInterest,
  trackFormInitiated,
  trackFormSuccess,
} from '@/tag-manager';
import { IDealer } from '@/types/dealer/dealer';
import { IDistrict } from '@/types/dealer/district';
import { IProvince } from '@/types/dealer/province';
import {
  IBody as IContentstackBody,
  IVehicleType,
} from '@/types/services/contentstack';
import { IBody as ILeadBody, IModel } from '@/types/services/lead';
import { IMy24Theme } from '@/types/shared';

declare const window: any;

export interface ILeadForm {
  title: string;
  campaignName: string;
  campaignId: string;
  campaignVersion: string;
  provinces?: IProvince[];
  successURL?: string;
  lcvModel?: IModel;
  fbModel?: string;
  vehicleType?: IVehicleType;
  showVehicleType?: boolean;
  isDialogForm?: boolean;
  interestType?: '' | 'อีซูซุลิสซิ่ง';
  remark?: '' | string;
  onChangePrivacy?: (name: string, checked: boolean) => void;
  onTriggerSubmit?: (isSuccess: boolean) => void;
  isSubmitDialog?: boolean;
  abTesting?: {
    experiment: string;
    variant: string;
  };
  theme?: IMy24Theme;
  dataTrackSection?: string;
}
interface IInput {
  value: string;
  pattern: RegExp;
  require: boolean;
  error: true | false | '';
}

interface IForm {
  fullname: IInput;
  mobile: IInput;
}

const View: React.FunctionComponent<ILeadForm> = (props: ILeadForm) => {
  const [viewRef, inView, isFirstRender] = useInViewRef({
    rootMargin: '0px 0px -1% 0px',
    once: false,
  });
  const formRef = useRef<HTMLFormElement | null>(null);
  const [form, setForm] = useState<IForm>({
    fullname: {
      value: '',
      pattern: REGEX_FULLNAME,
      require: true,
      error: false,
    },
    mobile: {
      value: '',
      pattern: REGEX_TEL_HOME_MOBILE,
      require: true,
      error: false,
    },
  });

  const [startValidate, setStartValidate] = useState<boolean>(false);
  const [availableTime, setAvailableTime] = useState<string>('ทุกช่วงเวลา');
  const [privacy, setPrivacy] = useState<boolean>(false);
  const [invalidSubmit, setInvalidSubmit] = useState<boolean>(false);
  const [provinceId, setProvinceId] = useState<string>('');
  const [districts, setDistricts] = useState<IDistrict[]>([]);
  const [dealerAll, setDealerAll] = useState<IDealer[]>([]);
  const [districtId, setDistrictId] = useState<string>('');
  const [dealers, setDealers] = useState<IDealer[]>([]);
  const [dealerId, setDealerId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [lcvModel, setLCVModel] = useState<IModel | undefined>(props.lcvModel);
  const router = useRouter();

  const _onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    switch (e.target.name) {
      case 'mobile':
        if (!REGEX_ONLY_NUMBER.test(e.target.value)) {
          return;
        }
        break;
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const _field: IInput = { ...form[e.target.name], value: e.target.value };
    const _form = { ...form };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    _form[e.target.name] = _field;

    if (startValidate) {
      validate(_form);
    }

    setForm(_form);
  };

  const _onProvinceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDealers([]);
    setDealerId('');
    setDistricts([]);
    setDistrictId('');
    setProvinceId(e.target.value);
  };

  useEffect(() => {
    if (!props.isDialogForm) return;
    if (props.isSubmitDialog) {
      _onSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isDialogForm, props.isSubmitDialog]);

  useEffect(() => {
    if (!provinceId) {
      return;
    }

    try {
      const getDistrict = async () => {
        const params: IContentstackBody = {
          route: 'getDistrictByProvinceID',
          id: provinceId,
        };
        const res = await clientContentStack({
          data: params,
        });
        res && res.data && setDistricts(res.data.entries);
      };
      getDistrict();
    } catch (e) {
      logger.error(e);
    }
  }, [provinceId]);

  useEffect(() => {
    if (!provinceId || !districts.length) {
      return;
    }

    const districtIDs = districtId
      ? districtId
      : districts.map((o) => o.uid).join(',');

    try {
      const getDealers = async () => {
        const params: IContentstackBody = {
          route: 'getDealerForLeadForm',
          provinceID: provinceId,
          districtIDs: districtIDs,
          vehicleType: props.vehicleType,
        };
        const res = await clientContentStack({
          data: params,
        });

        setDealers(res.data?.entries || []);
        if (!districtId) {
          setDealerAll(res.data?.entries || []);
        }
        if (res.data?.entries?.length == 1) {
          setDealerId(res.data?.entries?.[0]?.dealer_id);
        }
      };
      getDealers();
    } catch (e) {
      logger.error(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provinceId, districtId, districts]);

  const validate = (_form: IForm) => {
    let isValid = true;

    Object.keys(_form).forEach((key) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const input: IInput = _form[key];
      input.error = false;

      if (input.require) {
        if (input.value == '') {
          input.error = true;
          isValid = false;
          return;
        }

        if (!input.pattern.test(input.value)) {
          input.error = true;
          isValid = false;
          return;
        }
      } else {
        if (input.value != '') {
          if (!input.pattern.test(input.value)) {
            input.error = true;
            isValid = false;
            return;
          }
        }
      }
    });

    // dealerId
    if (props.provinces && props.provinces.length > 0 && !dealerId) {
      isValid = false;
    }

    return isValid;
  };

  useEffect(() => {
    if (!invalidSubmit) return;

    setInvalidSubmit(false);

    if (formRef.current) {
      const $form = formRef.current as HTMLFormElement;
      const $error = $form.querySelector('.is-error');
      const offset = $error?.classList.contains('is-tooltip') ? -60 : -16;
      if ($error) {
        props.isDialogForm
          ? $error.scrollIntoView()
          : scrollTo($error as HTMLElement, offset);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invalidSubmit]);

  useEffect(() => {
    if (isFirstRender) {
      trackFormInitiated({
        formId: props.campaignId,
        formName: props.campaignName,
        section: props.dataTrackSection && props.dataTrackSection,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  const [showToolTip, setShowToolTip] = useState<boolean>(false);
  const timeoutRef = useRef<NodeJS.Timeout | number>(null);
  const clearTooltipTimeout = () => {
    timeoutRef.current && clearTimeout(timeoutRef.current);
  };

  useEffect(() => {
    return () => clearTooltipTimeout();
  }, []);

  useEffect(() => {
    if (privacy) {
      setShowToolTip(false);
    }
  }, [privacy]);

  const _onSubmit = async () => {
    setStartValidate(true);

    if (!privacy) {
      clearTooltipTimeout();

      setShowToolTip(true);
      timeoutRef.current = setTimeout(() => {
        setShowToolTip(false);
      }, 4000);
    }

    const isValid = validate(form);
    if (!isValid || !privacy) {
      setInvalidSubmit(true);
      onSubmitDialogForm(false);
      return;
    }

    const _fullnameList = form.fullname.value.trim().split(' ');
    const _firstname = _fullnameList?.shift()?.trim();
    const _surname = _fullnameList.join(' ').trim();

    setLoading(true);
    try {
      const province = props?.provinces?.filter((o) => o.uid === provinceId);
      const district = districts.filter((o) => o.uid === districtId);
      const dealer = dealers.filter((o) => o.dealer_id === dealerId);

      const { campaignId } = props;

      const body: ILeadBody = {
        ctsCampaignName: props.campaignName,
        ctsCampaignId: campaignId,
        ctsCampaignVersion: props.campaignVersion,
        firstName: _firstname ?? '',
        surname: _surname,
        mobile: form.mobile.value,
        availableTime: availableTime,
        email: '',
        lineId: '',
        branchCode: dealer.length
          ? (dealer?.[0]?.mirai?.branch_code ?? '')
          : '',
        model: lcvModel,
        remark: props.remark && props.remark,
        experiment: props?.abTesting?.experiment,
        variant: props?.abTesting?.variant,
        ...getAdsTracking(),
        ...getSkipCallCenter(),
      };

      const RESPONSE = await clientLead({
        data: body,
      });

      if (process.env.NEXT_PUBLIC_WEB === 'lcv') {
        await trackLead(body);
      }

      const leadId = getLeadIDForAcessTrade(RESPONSE.data);

      switch (campaignId) {
        case ISUZU_CARE_FORM_ID:
          trackIsuzuCare(body);
          break;
        case TELETEC_FORM_ID:
          trackTeletec(body);
          break;
        case REGISTER_LCV_FORM_ID:
        case REGISTER_FORM_ID:
        case MAXFORCE_FORM_ID:
          trackFBLeadInterest({
            district: district.length ? district[0].name_th : '',
            city: province?.length ? province[0].name_th : '',
            dealerName: dealer?.length
              ? (dealer?.[0]?.mirai?.name_th ?? '')
              : '',
            id: dealerId,
            content_ids: props.campaignName,
            model: lcvModel ? formatModelForFacebookTracking(lcvModel) : '',
            leadId: leadId,
          });
          break;
        default:
          trackFBLeadFinancial({
            district: district.length ? district[0].name_th : '',
            city: province?.length ? province[0].name_th : '',
            dealerName: dealer?.length
              ? (dealer?.[0]?.mirai?.name_th ?? '')
              : '',
            id: dealerId,
            content_ids: props?.fbModel ?? '',
            leadId: leadId,
          });
          break;
      }
      trackFormSuccess({
        formId: campaignId,
        formName: props.campaignName,
        section: props.dataTrackSection && props.dataTrackSection,
      });

      Cookies.set(THANK, 'true', { expires: 10 / 86400 }); // 10 secs

      if (props.isDialogForm) {
        return onSubmitDialogForm(true);
      }

      props.successURL && router.push(props.successURL);
    } catch (e) {
      setLoading(false);
      logger.error(e);
    }
  };

  const filterDistrict = (districts: IDistrict[]) => {
    return districts.filter((district) => {
      let hasCV = false;
      if (!dealerAll || dealerAll.length == 0) return hasCV;
      dealerAll.forEach((dealer) => {
        dealer?.district?.forEach((o) => {
          if (o.uid == district.uid) {
            hasCV = true;
          }
        });
      });
      return hasCV;
    });
  };

  const onChangePrivacy = (name: string, checked: boolean) => {
    setPrivacy(checked);
    props.onChangePrivacy && props.onChangePrivacy(name, checked);
  };

  const onSubmitDialogForm = (isSuccess: boolean) => {
    if (props.isDialogForm) {
      return props.onTriggerSubmit && props.onTriggerSubmit(isSuccess);
    }
  };

  const handleChange = (name: string, value: string) => {
    setLCVModel(value as IModel);
  };

  useEffect(() => {
    const initialSearchParams = new URLSearchParams(window.location.search);
    switch (initialSearchParams.get('model')) {
      case 'ppv':
        setLCVModel('ppv');
        break;
      case 'pickup':
        setLCVModel('pickup');
        break;
    }
  }, []);

  return (
    <form ref={formRef as React.Ref<HTMLFormElement>}>
      <Grid gap="4" ref={viewRef as React.Ref<HTMLDivElement>}>
        {!!props.showVehicleType && (
          <Radio
            label="เลือกรุ่นรถ"
            name="model"
            variant={props.theme === 'dark' ? 'white' : 'black'}
            items={[
              {
                value: 'pickup',
                label: 'รถปิกอัพ',
                checked: lcvModel === 'pickup',
              },
              {
                value: 'ppv',
                label: 'รถมิว-เอ็กซ์',
                checked: lcvModel === 'ppv',
              },
            ]}
            onChangeInput={handleChange}
          />
        )}

        <Grid templateColumns={{ '@lg': '2' }} gap="4">
          <TextField
            label="ชื่อ - นามสกุล"
            name="fullname"
            type="text"
            placeholder="กรอกชื่อ - นามสกุล"
            value={form.fullname.value}
            disabled={loading}
            required
            className={form.fullname.error ? 'is-error' : ''}
            error={!!form.fullname.error}
            errorMessage={ERROR_MSG}
            onChange={_onChange}
            theme={props.theme}
          />
          <TextField
            id="input-mobile" // NOTE: use with tagmanager DOMELM_Lead_Mobile
            label="เบอร์โทรศัพท์"
            name="mobile"
            type="tel"
            maxLength={10}
            placeholder="กรอกเบอร์ของท่าน"
            value={form.mobile.value}
            disabled={loading}
            onChange={_onChange}
            required
            className={form.mobile.error ? 'is-error' : ''}
            error={!!form.mobile.error}
            errorMessage={ERROR_MSG}
            theme={props.theme}
            data-form-type="other" // NOTE: [TISW-10581] To prevent Dashlane extension popup that cause overflow layout bug
          />
        </Grid>

        {!!props.provinces && props.provinces.length > 0 && (
          <>
            <Box marginTop="4">
              <Text
                as="h3"
                font="bold"
                variant={
                  props.theme === 'dark' ? 'text-white' : 'text-gray-darkest'
                }
              >
                เลือกผู้จำหน่าย
              </Text>
            </Box>
            <Grid templateColumns={{ '@lg': '2' }} gap="4">
              <Select
                label="จังหวัด"
                required
                name="province"
                disabled={loading}
                onChange={_onProvinceChange}
                className={provinceId === '' && startValidate ? 'is-error' : ''}
                error={provinceId === '' && startValidate}
                theme={props.theme}
              >
                <option value="">เลือกจังหวัด</option>
                {props.provinces.map((o) => (
                  <option key={o.uid} value={o.uid}>
                    {o.name_th}
                  </option>
                ))}
              </Select>
              <Select
                label="อำเภอ"
                name="district"
                disabled={!districts.length || loading}
                onChange={(e) => setDistrictId(e.target.value)}
                theme={props.theme}
              >
                <option value="">เลือกอำเภอ</option>
                {filterDistrict(districts).map((o) => (
                  <option key={o.uid} value={o.uid}>
                    {o.name_th}
                  </option>
                ))}
              </Select>
            </Grid>
            <Grid templateColumns={{ '@lg': '2' }} gap="4">
              <Select
                label="ผู้จำหน่าย"
                required
                name="dealer"
                disabled={!dealers.length || loading}
                onChange={(e) => setDealerId(e.target.value)}
                value={dealerId}
                className={
                  dealerId === '' && startValidate && dealers.length
                    ? 'is-error'
                    : ''
                }
                error={dealerId === '' && !!startValidate && !!dealers.length}
                theme={props.theme}
              >
                <option value="">เลือกผู้จำหน่าย</option>
                {dealers.map((o) => (
                  <option key={o.dealer_id} value={o.dealer_id}>
                    {o?.mirai?.name_th}
                  </option>
                ))}
              </Select>
              <Select
                label="เวลาที่สะดวกให้ติดต่อ"
                name="available_time"
                disabled={loading}
                value={availableTime}
                onChange={(e) => setAvailableTime(e.target.value)}
                theme={props.theme}
              >
                {AVAILABLE_TIME_OPTIONS.map((o, i) => (
                  <option key={i} value={o}>
                    {o}
                  </option>
                ))}
              </Select>
            </Grid>
          </>
        )}
        <Box
          position="relative"
          css={{
            '& a': {
              color: props.theme === 'dark' ? '$primary200' : '$primary',
            },
          }}
        >
          <Checkbox
            name="consent"
            disabled={loading}
            onChangeInput={onChangePrivacy}
            className={startValidate && !privacy ? 'is-error is-tooltip' : ''}
            error={!!startValidate && !privacy}
            theme={props.theme}
            items={[
              {
                name: 'accept',
                checked: privacy,
                children: (
                  <>
                    อ่านและยอมรับ&nbsp;
                    <Link href="/privacy-policy" target="_blank">
                      นโยบายความเป็นส่วนตัว
                    </Link>
                  </>
                ),
              },
            ]}
          />
          {!!showToolTip && (
            <Tooltip
              theme={props.theme}
              msg="กรุณากดยอมรับ นโยบายความเป็นส่วนตัว"
            />
          )}
        </Box>

        {!props.isDialogForm && (
          <Box>
            <Button
              name="submit"
              label="ยืนยัน"
              isLoading={loading}
              onClick={_onSubmit}
              isFull={{ '@maxlg': true }}
              tracking={{
                dataTrack: 'lead-form',
                dataTrackText: 'ยืนยัน',
                dataTrackUrl: props.successURL,
              }}
              test={{
                dataTest: 'button_submit',
              }}
            />
          </Box>
        )}
      </Grid>
    </form>
  );
};
View.displayName = 'SharedFormLeadForm';
export default View;
